import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { LoadingSpinner } from "@/components/ui/loadingSpinner";
import { useFetchData } from "@/hooks/useFetchData";
import { CheckCircle, FolderOpen } from "lucide-react";
import * as React from "react";
import { Suspense } from "react";
import FundingPie from "./FundingPie";
import FundingSummary from "./FundingSummary";
import StatusFinancement from "./StatusFinancement";
import TableItem from "./TableItem";
import Totals from "./Totals";

type Program = {
  program: {
    id: number;
    name: string;
  };
  projects: any;
};
const financersCache = {};
const DataTableCommercial = ({
  setProjects,
  projectsByPp,
  defaultOpened,
  setDefaultOpened,
  columnVisibility,
  setColumnVisibility,
  loadingProjectsFiltered,
}: {
  setProjects: React.Dispatch<React.SetStateAction<any[]>>;
  projectsByPp: Record<string, Program>;
  defaultOpened: string[];
  setDefaultOpened: (value: any) => void;
  columnVisibility: any;
  setColumnVisibility: React.Dispatch<React.SetStateAction<any>>;
  loadingProjectsFiltered: boolean;
}) => {
  // const firstProgramId = Object.values(projectsByPp)[0]?.program?.id;
  const [rowSelections, setRowSelections] = React.useState<number[]>([]);
  const countProjectsInAllPrograms = Object.values(projectsByPp).reduce(
    (total, program) => total + (program.projects?.length || 0),
    0
  );
  // get params
  const searchParams = new URLSearchParams(window.location.search);
  const carrier = searchParams.get("carrier");
  const program = searchParams.get("program");
  const [dataReport, setDataReport] = React.useState(null);
  const [loading, setLoading] = React.useState(false);
  const { fetchData } = useFetchData();
  /* Financeurs */
  const [financers, setFinancers] = React.useState([]);
  const [loadingFinancers, setLoadingFinancers] = React.useState(false);
  React.useEffect(() => {
    const fetchFinancers = async () => {
      setLoadingFinancers(true);
      try {
        // Vérifier si les données sont déjà dans le cache
        if (financersCache[import.meta.env.VITE_API_ENDPOINT + "/financers"]) {
          setFinancers(
            financersCache[import.meta.env.VITE_API_ENDPOINT + "/financers"]
          );
        } else {
          const res = await fetchData(
            `${import.meta.env.VITE_API_ENDPOINT}/financers`
          );
          setFinancers(res);
          // Mettre les données dans le cache
          financersCache[import.meta.env.VITE_API_ENDPOINT + "/financers"] =
            res;
        }
      } finally {
        setLoadingFinancers(false);
      }
    };

    fetchFinancers();
  }, []);

  const [devises, setDevises] = React.useState([]);
  React.useEffect(() => {
    const fetchDevises = async () => {
      setLoading(true);
      const res = await fetchData(
        `${import.meta.env.VITE_API_ENDPOINT}/devises`
      );
      setDevises(res);
      setLoading(false);
    };
    fetchDevises();
  }, []);

  React.useEffect(() => {
    if (Array.isArray(devises)) {
      localStorage.setItem("devises", JSON.stringify(devises));
    }
  }, [devises]);

  React.useEffect(() => {
    (async () => {
      setLoading(true);

      if (!projectsByPp || Object.keys(projectsByPp).length === 0) {
        setLoading(false);
        return;
      }

      const fetchReportPromise = fetchData(
        `${import.meta.env.VITE_API_ENDPOINT}/report/programs${
          carrier
            ? `?user=${carrier}&context=program`
            : program
            ? `?program=${program}&context=program`
            : "?context=program"
        }`
      );

      // On attend les deux résultats en parallèle
      const [reportData] = await Promise.all([fetchReportPromise]);

      setDataReport(reportData);

      setLoading(false);
    })();
  }, [projectsByPp, devises, carrier]);

  if (loading) {
    return (
      <div className="mt-4">
        <LoadingSpinner />
      </div>
    );
  }

  const checkStatus = (program) => {
    const hasDesired = program.projects.some(
      (p) => p.desiredFunding.length > 0 && p.soldStatus !== 2
    );
    const hasObtained = program.projects.some(
      (p) => p.obtainedFunding.length > 0
    );

    if (hasObtained && hasDesired) return 2;
    if (hasDesired) return 1;
    return 3;
  };

  const idProject = searchParams.get("idproject"); // Récupérer la valeur de idproject

  return (
    <div>
      <div className="flex md:flex-row flex-col flex-wrap mt-4">
        <FundingSummary
          carrier={carrier}
          devises={devises}
          projectsByPp={projectsByPp}
        />
        <div className="md:w-[20%] md:ml-auto relative">
          <FundingPie
            carrier={carrier}
            devises={devises}
            projectsByPp={projectsByPp}
            devise="€"
            type={2}
            className={"h-[140px] w-[100%] "}
          />
        </div>
      </div>
      {loadingProjectsFiltered && Object.keys(projectsByPp).length === 0 ? (
        <LoadingSpinner />
      ) : (
        <Accordion
          type="multiple"
          className="w-full"
          value={defaultOpened}
          onValueChange={setDefaultOpened}
        >
          {Object.values(projectsByPp).map((program: Program, key) => (
            <AccordionItem
              value={String(program.program.id)}
              className="border-0 relative mb-2"
              key={key}
            >
              <AccordionTrigger
                className="py-[10px] hover:no-underline group"
                classNameChevron="absolute left-[10px] top-[33px] -translate-y-1/2"
              >
                <div className="flex justify-between items-start w-full bg-muted pl-[35px] pr-[18px] py-[10px] rounded-[9px] relative">
                  <div className="flex items-center gap-2 flex-1 text-left ">
                    <FolderOpen />
                    <div className="flex-1 group-hover:underline">
                      PP - {program.program.carrier.fullname}
                    </div>
                  </div>
                  <div className="absolute md:static top-[-10px] right-[-10px] scale-75 md:scale-100">
                    <StatusFinancement
                      status={(() => {
                        return checkStatus(program);
                      })()}
                    />
                  </div>
                  {program.projects.some(
                    (project) => project.id.toString() === idProject
                  ) &&
                    !defaultOpened.includes(String(program.program.id)) && (
                      <span className="absolute top-1/2 -translate-y-1/2 right-[-20px] flex size-6 z-10 items-center justify-center">
                        <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-green-700 opacity-75"></span>
                        <CheckCircle className=" text-green-700 animate-pulse" />
                      </span>
                    )}
                </div>
              </AccordionTrigger>
              <AccordionContent>
                <TableItem
                  program={program}
                  key={key}
                  dataReport={dataReport}
                  rowSelections={rowSelections}
                  setRowSelections={setRowSelections}
                  columnVisibility={columnVisibility}
                  setColumnVisibility={setColumnVisibility}
                  setProjects={setProjects}
                  loadingFinancers={loadingFinancers}
                  financers={financers}
                />
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      )}
      <div>{countProjectsInAllPrograms} résultats</div>

      <Suspense fallback={<LoadingSpinner />}>
        <Totals
          columnVisibility={columnVisibility}
          dataReport={dataReport}
          projectsByPp={projectsByPp}
          rowSelections={rowSelections}
          setRowSelections={setRowSelections}
        />
      </Suspense>
    </div>
  );
};

export default DataTableCommercial;
